function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { useGesture } from "@use-gesture/react";
import styled from "styled-components";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import map from "astrid-helpers/src/map";
import useBoundingClientRect from "astrid-hooks/src/useBoundingClientRect";
import List from "../../Data/List";
import Page from "./Page";
import optimizeRects from "./optimizeRects";
import splitWithIndex from "./splitWithIndex";
import { getDirection } from "./state/direction";
import { getPage, setPage, usePage } from "./state/page";
import { useNumPages } from "./state/pages";
import { usePdf } from "./state/pdf";
import { getScale, setScale, useScale } from "./state/scale";
import { getScrollOffset, setScrollOffset, useScrollOffset } from "./state/scrollOffset";
import { getSelection, setSelection } from "./state/selection";
import { setSize } from "./state/size";
function getIndex(node) {
  let i = 0;
  while ((node = node.previousSibling) != null) ++i;
  return i;
}
function getOffset(node, x, dir = "ltr", include = false) {
  const rect = node.getBoundingClientRect();
  const {
    innerText
  } = node;
  const length = innerText.length;
  let offset = (x - rect.left) / rect.width * length;
  if (dir === "rtl") {
    offset = length - offset;
  }
  const words = splitWithIndex(innerText, " ");
  const word = words.find(([index]) => index < offset);
  return word ? word[0] + (include ? word[1].length : 0) : 0;
}
const types = ["SPAN", "MARK"];
const Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-1mw5ek1-0"
})(["overflow:hidden;height:100%;"]);
const Item = /*#__PURE__*/memo(({
  index,
  style
}) => /*#__PURE__*/React.createElement(Page, {
  index: index,
  style: style
}));
const Pdf = /*#__PURE__*/memo(({
  offset
}) => {
  const pdf = usePdf();
  const scale = useScale();
  const numPages = useNumPages();
  const [page, autoScrollPage] = usePage();
  const [scrollOffset, autoScrollOffset] = useScrollOffset();
  const ref = useRef();
  const listRef = useRef();
  const disabled = useState(false);
  const [zoom, setZoom] = useState(1);
  const {
    height
  } = useBoundingClientRect(ref);
  const size = height * scale;
  const onScroll = useCallback(({
    scrollOffset
  }) => {
    const page = Math.floor(map(scrollOffset + offset, 0, numPages * size, 1, numPages + 1));
    if (page !== getPage()) {
      setPage(page, false);
    }
    setScrollOffset([(scrollOffset + height / 2) / size, false]);
  }, [height, offset, numPages, size]);
  const onDrag = useCallback(({
    xy,
    tap,
    axis,
    last,
    first,
    event,
    initial
  }) => {
    event.stopPropagation();
    if (first) {
      // setSelection();
    } else if (axis === "x" || tap) {
      const firstNode = document.elementFromPoint(...initial);
      const lastNode = document.elementFromPoint(...xy);
      if (!tap && !disabled.current) {
        disabled.current = true;
        listRef.current._outerRef.style.overflowY = "hidden";
      }
      if (firstNode && lastNode && types.includes(firstNode.nodeName) && types.includes(lastNode.nodeName)) {
        const dir = getDirection();
        const range = document.createRange();
        const selection = window.getSelection();
        const firstIndex = getIndex(firstNode);
        const lastIndex = getIndex(lastNode);
        const forwards = firstIndex < lastIndex || firstIndex === lastIndex && (dir === "ltr" ? initial[0] < xy[0] : xy[0] < initial[0]);
        const startNode = forwards ? firstNode : lastNode;
        const startOffset = getOffset(startNode, forwards ? initial[0] : xy[0], dir);
        const stopNode = forwards ? lastNode : firstNode;
        const stopOffset = getOffset(stopNode, forwards ? xy[0] : initial[0], dir, true);
        const parent = firstNode.parentNode;
        const parentRect = parent.getBoundingClientRect();
        const page = parseInt(parent.dataset.page);
        range.setStart(startNode.firstChild, startOffset);
        range.setEnd(stopNode.firstChild, stopOffset);
        selection.removeAllRanges();
        selection.addRange(range);
        const text = range.toString().replace("  ", " ");
        const rects = optimizeRects(Array.from(range.getClientRects())).map(rect => ({
          width: rect.width / parentRect.width,
          height: rect.height / parentRect.height,
          top: (rect.top - parentRect.top) / parentRect.height,
          left: (rect.left - parentRect.left) / parentRect.width
        }));
        setSelection({
          page,
          text,
          rects
        });
      } else {
        setSelection();
      }
    }
    if (last) {
      if (getSelection()) {
        setSelection(selection => ({
          ...selection,
          done: true
        }));
      }
      if (disabled.current) {
        disabled.current = false;
        listRef.current._outerRef.style.overflowY = "overlay";
      }
    }
  }, [disabled]);
  const onPinch = useCallback(({
    event,
    memo,
    da: [d]
  }) => {
    event.stopPropagation();
    if (memo) {
      setZoom(z => z * (d / memo));
    }
    return d;
  }, []);
  const onPinchEnd = useCallback(() => setScale(getScale() * zoom), [zoom]);
  const onGesture = useGesture({
    onDrag,
    onPinch,
    onPinchEnd
  });
  useEffect(() => {
    setZoom(1);
    setSize(size - 10);
  }, [size]);
  useEffect(() => {
    if (listRef.current) {
      const [offset] = getScrollOffset();
      listRef.current.scrollTo(Math.floor(offset * size - height / 2));
    }
  }, [height, size]);
  useEffect(() => {
    if (autoScrollOffset && listRef.current && scrollOffset) {
      listRef.current.scrollTo(Math.floor(scrollOffset * size - height / 2));
    }
  }, [autoScrollOffset, height, scrollOffset, size]);
  useEffect(() => {
    if (autoScrollPage && listRef.current && page) {
      setPage(page, false);
      listRef.current.scrollToItem(page - 1, "start");
    }
  }, [autoScrollPage, page]);
  useEffect(() => {
    if (listRef.current) {
      listRef.current._outerRef.style.transform = zoom === 1 ? "none" : `scale(${zoom})`;
    }
  }, [zoom]);
  return /*#__PURE__*/React.createElement(Root, _extends({
    ref: ref
  }, onGesture()), pdf && numPages > 0 && height > 0 && /*#__PURE__*/React.createElement(List.Fixed, {
    ref: listRef,
    width: "100%",
    height: height,
    itemSize: size,
    overscanCount: 0,
    onScroll: onScroll,
    itemCount: numPages,
    initialScrollOffset: (page - 1) * size,
    style: {
      overflowX: "hidden",
      overflowY: "overlay"
    }
  }, Item));
});
Pdf.defaultProps = {
  offset: 70
};
export default Pdf;
//# sourceMappingURL=Pdf.js.map